import { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { api } from "@/lib/api";
import { Eye } from "lucide-react";

export const useBillingOn = (user) => {
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (user?.role === "franchisee_employee") { api("/billing/my-schedule").then((r) => setOn(!!r.enabled)).catch(() => {}); return; }
    if (user?.role !== "franchisee" || user?.actual_role) return;
    api("/billing/status").then((r) => { setMoneyCurrency(r.currency); setOn(!!r.enabled); }).catch(() => {});
  }, [user?.id, user?.role, user?.actual_role]);
  return on;
};

const shortWhen = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
export const OpenedBadge = ({ views, testId, long = false }) => (views?.count ? (
  <span data-testid={testId} title={`First opened ${shortWhen(views.first_at)}`} className="inline-flex items-center gap-1 rounded-full bg-[rgba(76,204,6,0.15)] px-2 py-0.5 text-[10px] font-bold text-[var(--accent)]">
    <Eye size={11} />{long ? `Customer opened it ${views.count}× · last ${shortWhen(views.last_at)}` : `Opened ${views.count}×`}
  </span>) : null);

export const leadsChanged = () => window.dispatchEvent(new Event("alr-leads-changed"));

export const useNewLeads = (on) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!on) return undefined;
    const load = () => api("/billing/leads/new-count").then((r) => setN(r.count || 0)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("alr-leads-changed", load);
    return () => { clearInterval(t); window.removeEventListener("alr-leads-changed", load); };
  }, [on]);
  return n;
};

export const CountBadge = ({ n, testId, className = "" }) => (n > 0 ? <span data-testid={testId} className={`inline-flex min-w-[20px] items-center justify-center rounded-full bg-[#FF3B30] px-1.5 text-[11px] font-extrabold leading-5 text-white ${className}`}>{n > 99 ? "99+" : n}</span> : null);

let CUR = "usd";
export const setMoneyCurrency = (c) => { CUR = (c || "usd").toLowerCase(); };
export const getMoneyCurrency = () => CUR;
// money(n) uses the location's currency; money(n, "usd") forces USD (shown as US$ for Canadian locations).
export const money = (n, cur) => {
  const c = (cur || CUR).toLowerCase();
  const sym = c === "cad" ? "CA$" : CUR === "cad" ? "US$" : "$";
  return `${Number(n) < 0 ? "-" : ""}${sym}${Math.abs(Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
export const usdOf = (e, amt) => (e.currency === "cad" && e.fx_rate ? Math.round(amt * e.fx_rate * 100) / 100 : amt);
export const FxNote = ({ e, testId }) => (e.currency === "cad" ? <p className="text-[11px] text-[#A0A0A0]" data-testid={testId}>{e.fx_rate ? `Converted from ${money(e.amount - (e.credit_applied || 0), "cad")} at the Bank of Canada rate of ${e.fx_cad_per_usd} CAD per USD (${e.fx_date})` : "Waiting for the Bank of Canada rate for this day; the USD amount is drafted once it's published"}</p> : null);
export const parseDay = (iso) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso)); return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(iso); };
export const fmtDate = (iso) => (iso ? parseDay(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");
export const inputCls = "w-full rounded-lg border border-[#333] bg-[#121212] px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-[var(--accent)]";

export const CATEGORIES = [
  { value: "service", label: "Service (royalty applies)" },
  { value: "travel", label: "Hotel / travel (receipt required)" },
  { value: "equipment_rental", label: "Equipment rental (rental receipt required)" },
];

export const previewTotals = (d) => {
  const inc = (d.lines || []).filter((l) => !l.optional || l.selected);
  const services = inc.reduce((s, l) => s + (+l.quantity || 0) * (+l.unit_price || 0), 0);
  const dv = +(d.discount?.value || 0);
  const discount = services > 0 ? Math.min(services, d.discount?.type === "pct" ? services * Math.min(dv, 100) / 100 : dv) : 0;
  const f = d.fees || {};
  const disposal = f.disposal_on ? (services - discount) * Math.min(+f.disposal_pct || 0, 2) / 100 : 0;
  const lens = f.lens_on ? Math.min(+f.lens_qty || 0, 5) * Math.min(+f.lens_price || 0, 40) : 0;
  const subtotal = services - discount + disposal + lens;
  const tax = subtotal * (+d.tax_rate || 0) / 100;
  return { services, discount, disposal_fee: disposal, lens_fee: lens, subtotal, tax, total: subtotal + tax };
};

export const STATUS_COLORS = {
  new: "#39A0FF", quoted: "#4CCC06", archived: "#666",
  draft: "#9A9A9A", sent: "#39A0FF", approved: "#4CCC06", converted: "#A0A0A0", declined: "#FF3333",
  partial: "#FFB800", paid: "#4CCC06", void: "#666", unscheduled: "#9A9A9A", scheduled: "#39A0FF",
  in_progress: "#FFB800", completed: "#4CCC06", queued: "#9A9A9A", processing: "#39A0FF", succeeded: "#4CCC06",
  failed: "#FF3333", invoiced: "#FFB800", needs_attention: "#FF3333", paid_invoice: "#4CCC06", credit: "#A0A0A0",
  active: "#4CCC06", pending: "#FFB800", none: "#9A9A9A", started: "#FFB800",
  pending_ai: "#39A0FF", needs_review: "#FFB800", rejected: "#FF3333",
};

export const STATUS_LABELS = {
  in_progress: "In progress", needs_attention: "Needs attention", paid_invoice: "Paid (invoice)", pending_ai: "AI checking",
  needs_review: "ALR reviewing", invoiced: "QuickBooks invoice sent", processing: "Drafting", succeeded: "Collected", none: "Not connected",
  started: "Setup started", active: "Connected",
};

export const StatusPill = ({ status, testId }) => {
  const c = STATUS_COLORS[status] || "#9A9A9A";
  const label = STATUS_LABELS[status] || (status || "").replace(/_/g, " ").replace(/^\w/, (m) => m.toUpperCase());
  return <span data-testid={testId} className="inline-flex w-fit items-center rounded-full border px-2.5 py-[3px] text-[11px] font-bold" style={{ borderColor: c, color: c }}>{label}</span>;
};

let stripeCache = {};
export const getStripe = (pk) => (stripeCache[pk] ||= loadStripe(pk));
